import { useNavigate, useRouterState } from '@tanstack/react-router';
import isCountryWithBanner from 'components/betting-warning-banner/utils/is-country-with-banner';
import { Box } from 'components/Box';
import { Button } from 'components/Button';
import CompactNavbar from 'components/CompactNavbar/CompactNavbar.component';
import ExpandedNavBar from 'components/ExpandedNavbar/ExpandedNavbar.component';
import ExternalLink from 'components/ExternalLink/ExternalLink.component';
import Icon from 'components/Icon/Icon.component';
import {
  ToolbarButtonProps,
  VariantComponentProps,
} from 'components/SignedInUserToolbar/SignedInUserToolbar.type';
import SlideInMenu from 'components/SlideInMenu/SlideInMenu.component';
import { SlideInMenuVariant } from 'components/SlideInMenu/SlideInMenu.config';
import { MAIN_SITE_HOMEPAGE } from 'config';
import ChevronLeft from 'images/icons/chevron-left.svg?react';
import emptyFavoriteSrc from 'images/icons/pink-heart-empty.svg';
import fullFavoriteSrc from 'images/icons/pink-heart-full.svg';
import refreshWhiteSrc from 'images/icons/refresh-white.svg?react';
import userIconWhiteSrc from 'images/icons/user-white.svg?react';
import UserIconComponent from 'images/icons/user.svg?react';
import React from 'react';
import { Translate } from 'react-localize-redux';
import { batch, useDispatch, useSelector, useStore } from 'react-redux';
import { requestDataIfNecessary } from 'services/api/api.actions';
import {
  selectNavbarContent,
  selectNavbarContentApiConfig,
} from 'services/cmsContent/cmsContent.selectors';
import {
  makeSelectPostFavoritesApiConfig,
  selectFavorites,
} from 'services/favorites/favorites.selectors';
import { makeSelectPostGrantApiConfig } from 'services/grant/grant.selectors';
import { useI18n } from 'services/i18n/i18n.hooks';
import { requestInboxStatsIfNecessary } from 'services/inbox/inbox.action';
import {
  selectAuth,
  selectIsSessionValidated,
  selectVerifySessionEndpointApiConfig,
} from 'services/session/session.selectors';
import { requestUserBalanceIfNecessary } from 'services/user/user.action';
import { selectUserBalance } from 'services/user/user.selectors';
import colors from 'style-utils/colors';
import createExtractCssFromProps from 'style-utils/createExtractCssFromProps';
import { withMargin, withPadding } from 'style-utils/dimensionsMixins';
import { withOneLineEllipsis } from 'style-utils/overflow';
import styled, { css, StyledProps } from 'styled-components';
import { idTracingMap } from 'utils/config.config';
import { useIsAtLeastDesktopBreakpoint } from 'utils/hooks/media-queries';
import useGetTranslate from 'utils/hooks/useGetTranslate';
import { LogoutBtn } from './components/logout-btn';
import NotSignedInUserFallback from './partials/NotSignedUserFallback.component';
import {
  AppHeading,
  SiteSection,
  TopBarRoot,
  WithBannerTopBarRoot,
} from './style/Navbar.style';

/**
 * Legacy version of the Navbar, to be used until all platforms support the Moira
 * service /getBonusSpendability. Remove the whole component once the new service
 * is supported by all platforms and the new Navbar is the default one.
 */
export default function NavbarLegacy() {
  const reduxStore = useStore();
  const navbarContent = useSelector(selectNavbarContent);
  const isAtLeastDesktopBreakpoint = useIsAtLeastDesktopBreakpoint();
  const isUserLoggedIn = useSelector(selectIsSessionValidated);
  const TopBarRootComponent = isCountryWithBanner()
    ? WithBannerTopBarRoot
    : TopBarRoot;

  const handleGoBackButton = () => {
    window.location.assign(MAIN_SITE_HOMEPAGE);
  };

  React.useEffect(() => {
    const apiConfig = selectNavbarContentApiConfig(reduxStore.getState());

    reduxStore.dispatch(requestDataIfNecessary(apiConfig));
  }, [reduxStore]);

  if (isAtLeastDesktopBreakpoint) {
    return (
      <TopBarRootComponent>
        <ExpandedNavBar navbarContent={navbarContent}>
          {isUserLoggedIn ? (
            <SignedInUserToolbar variant="expanded" />
          ) : (
            <NotSignedInUserFallback padding="0 16px" />
          )}
        </ExpandedNavBar>
        <SlideInMenu
          navbarContent={navbarContent}
          variant={SlideInMenuVariant.expanded}
        />
      </TopBarRootComponent>
    );
  }

  return (
    <TopBarRootComponent>
      <CompactNavbar navbarContent={navbarContent}>
        {isUserLoggedIn ? (
          <SignedInUserToolbar variant="compact" />
        ) : (
          <NotSignedInUserFallback />
        )}
      </CompactNavbar>
      <SiteSection>
        <Button
          variant="ghost"
          onClick={handleGoBackButton}
          padding={'0'}
          height={'25px'}
          aria-label="previous page"
        >
          <Icon svgIconComponent={ChevronLeft} fill="currentColor" />
        </Button>
        <AppHeading>
          <Translate id="lbl.mainHeading" />
        </AppHeading>
        {isUserLoggedIn && <LogoutBtn />}
      </SiteSection>
      <SlideInMenu
        navbarContent={navbarContent}
        variant={SlideInMenuVariant.compact}
      />
    </TopBarRootComponent>
  );
}

export function SignedInUserToolbar({
  variant,
}: {
  variant: 'expanded' | 'compact';
}) {
  const userBalance = useSelector(selectUserBalance);

  const {
    formatters: { formatCurrency },
  } = useI18n();

  const dispatch = useDispatch();

  const handleRefreshButtonClick = React.useCallback(
    () =>
      batch(() => {
        dispatch(requestInboxStatsIfNecessary());
        dispatch(requestUserBalanceIfNecessary());
      }),
    [dispatch],
  );

  const realBonus = userBalance.bonusTypeBalances?.find(
    ({ bonusType }) => bonusType === 'BS',
  )?.balance;
  const formattedBonus = formatCurrency(realBonus ?? 0);

  const formattedBalance = formatCurrency(userBalance.realCashBalance);

  switch (variant) {
    case 'compact':
      return (
        <CompactVariant
          formattedBalance={formattedBalance}
          formattedBonus={formattedBonus}
        />
      );
    case 'expanded':
      return (
        <ExpandedVariant
          onRefreshClick={handleRefreshButtonClick}
          formattedBalance={formattedBalance}
          formattedBonus={formattedBonus}
        />
      );
  }
}

function CompactVariant({
  formattedBalance,
  formattedBonus,
}: VariantComponentProps) {
  const navbarContent = useSelector(selectNavbarContent);
  const { location } = useRouterState();
  const t = useGetTranslate();
  const navigate = useNavigate();
  const reduxStore = useStore();
  const handleChangeLocationToDashboard = () => {
    if (location.pathname !== '/') {
      navigate({ to: '/' });
    }
  };

  React.useEffect(() => {
    const postFavoritesApiConfig = makeSelectPostFavoritesApiConfig()(
      reduxStore.getState(),
    );
    reduxStore.dispatch(requestDataIfNecessary(postFavoritesApiConfig));
  }, [reduxStore]);

  return (
    <Box display={'flex'} alignItems={'center'} height={'100%'}>
      <BonusLink
        noReferrer={false}
        target="_self"
        href={navbarContent.linkMyBonus}
      >
        <NotificationLabel textAlign="center">
          {navbarContent.labelMyBonus}
        </NotificationLabel>
        <CurrencyLabel>{formattedBonus || ' '}</CurrencyLabel>
      </BonusLink>
      <VericalRuler />
      <BalanceDisplay>
        <NotificationLabel textAlign="right">
          {navbarContent.labelMyBalance}
        </NotificationLabel>
        <CurrencyLabel>{formattedBalance || ' '}</CurrencyLabel>
      </BalanceDisplay>
      <ToolbarButton
        type="button"
        padding="0 24px 0 8px"
        onClick={handleChangeLocationToDashboard}
        title={t('reservedArea').toString()}
      >
        <Icon svgIconComponent={UserIconComponent} />
      </ToolbarButton>
    </Box>
  );
}

function ExpandedVariant({
  formattedBalance,
  formattedBonus,
  onRefreshClick,
}: VariantComponentProps & { onRefreshClick: () => void }) {
  const reduxStore = useStore();
  const navbarContent = useSelector(selectNavbarContent);
  const favorites = useSelector(selectFavorites);
  const navigate = useNavigate();
  const { location } = useRouterState();
  const t = useGetTranslate();

  const handleChangeLocationToDashboard = () => {
    if (location.pathname !== '/') {
      navigate({ to: '/' });
    }
  };

  const onRechargeClick = () => navigate({ to: '/topup' });

  const auth = useSelector(selectAuth);

  React.useEffect(() => {
    const verifySessionApiConfig = selectVerifySessionEndpointApiConfig(
      reduxStore.getState(),
    );

    reduxStore.dispatch(requestDataIfNecessary(verifySessionApiConfig));
  }, [reduxStore]);

  React.useEffect(() => {
    if (auth.requestId) {
      const postGrantApiConfig = makeSelectPostGrantApiConfig({
        requestId: auth.requestId,
      })(reduxStore.getState());
      reduxStore.dispatch(requestDataIfNecessary(postGrantApiConfig));
    }
  }, [auth.requestId, reduxStore]);

  React.useEffect(() => {
    const postFavoritesApiConfig = makeSelectPostFavoritesApiConfig()(
      reduxStore.getState(),
    );
    reduxStore.dispatch(requestDataIfNecessary(postFavoritesApiConfig));
  }, [reduxStore]);

  return (
    <Box
      display={'flex'}
      alignItems={'center'}
      height={'100%'}
      padding="0 20px"
      // Avoiding css variable only because this component will hopefully be removed
      // soon and we don't want to add technical debt to the new Navbar by making
      // the styles more complex than necessary
      backgroundColor={'black'}
      color={'white'}
    >
      <BonusLink
        noReferrer={false}
        target="_self"
        href={navbarContent.linkMyBonus}
      >
        <NotificationLabel textAlign="center">
          {navbarContent.labelMyBonus}
        </NotificationLabel>
        <CurrencyLabel
          data-qa="user-toolbar-bonus"
          id={idTracingMap.SignedInUserToolbar.EV_RealBonus}
        >
          {formattedBonus || ' '}
        </CurrencyLabel>
      </BonusLink>
      <VericalRuler />
      <BalanceDisplay>
        <NotificationLabel textAlign="right">
          {navbarContent.labelMyBalance}
        </NotificationLabel>
        <CurrencyLabel
          data-qa="user-toolbar-balance"
          id={idTracingMap.SignedInUserToolbar.EV_Balance}
        >
          {formattedBalance || ' '}
        </CurrencyLabel>
      </BalanceDisplay>
      <Button
        data-qa="user-toolbar-recharge-button"
        id={idTracingMap.SignedInUserToolbar.EV_RechargeButton}
        height={'24px'}
        minWidth={'76px'}
        onClick={onRechargeClick}
      >
        <Translate id={'lbl.modalrecharge.recharge'} />
      </Button>

      <ToolbarButton
        id={idTracingMap.SignedInUserToolbar.EV_RefreshButton}
        type="button"
        padding="0 5px 0 5px"
        margin="0 3px 0 6px"
        onClick={onRefreshClick}
        data-qa="user-toolbar-refresh-button"
      >
        <Icon svgIconComponent={refreshWhiteSrc} />
      </ToolbarButton>
      <ExternalLink
        href={navbarContent?.linkFavorite || ''}
        noReferrer={false}
        target={'_self'}
        data-qa="user-toolbar-favorites-link"
        anchorElement={
          <ToolbarButton
            id={idTracingMap.SignedInUserToolbar.EV_HeartButton}
            padding="0 5px 0 5px"
            margin="0 3px 0 3px"
            as="a"
          />
        }
      >
        <Icon iconSrc={favorites ? fullFavoriteSrc : emptyFavoriteSrc} />
      </ExternalLink>
      <ToolbarButton
        id={idTracingMap.SignedInUserToolbar.EV_ProfileButton}
        type="button"
        padding="0 5px 0 5px"
        margin="0 3px 0 3px"
        onClick={handleChangeLocationToDashboard}
        title={t('reservedArea').toString()}
        data-qa="user-toolbar-profile-button"
      >
        <Icon svgIconComponent={userIconWhiteSrc} />
      </ToolbarButton>
    </Box>
  );
}

const NotificationLabel = styled.span`
  display: inline-block;
  width: 100%;
  font-size: 10px;
  letter-spacing: inherit;
  ${withOneLineEllipsis};
  ${createExtractCssFromProps({ textAlign: 'text-align' })};
`;

const CurrencyLabel = styled.span`
  display: inline-block;
  width: 100%;
  letter-spacing: inherit;
  font-size: 12px;
  flex: 0 0 auto;
  font-weight: bold;
  text-align: right;
  text-decoration: underline;
  ${withOneLineEllipsis};
`;

const notificationSectionStyles = css`
  display: flex;
  overflow: hidden;
  max-width: 120px;
  height: 100%;
  flex-flow: column nowrap;
  justify-content: center;
  color: inherit;
  flex: 0 1 auto;
  text-decoration: none;
  margin: 0;
  & > * {
    flex: 0 0 auto;
  }
`;

const BonusLink = styled(ExternalLink)`
  ${notificationSectionStyles};
`;

const BalanceDisplay = styled.div`
  ${notificationSectionStyles};
  padding: 0 12px 0 0;

  & > ${CurrencyLabel} {
    text-decoration: none;
  }
`;

const VericalRuler = styled.hr`
  height: 19.5px;
  width: 1px;
  background: currentColor;
  border: none;
  padding: 0;
  flex: 0 0 auto;
  margin: 0 11.5px;
`;

const ToolbarButton = styled.button<StyledProps<ToolbarButtonProps>>`
  display: inline-block;
  line-height: 1;
  margin: 0;
  border: none;
  outline: none;
  cursor: pointer;
  color: inherit;
  ${withPadding};
  ${withMargin};
  background: ${colors.transparent};
`;
